import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import logo from '../../assets/logoHeader.png';

const extractPayload = (payload) => {
  if (!payload) return null;
  return payload?.data?.data ?? payload?.data ?? payload;
};

const normalizeTracking = (payload) => extractPayload(payload) || null;

const formatDateDisplay = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString();
};

const TrackingPage = () => {
  const { t } = useLanguage();
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    let cancelled = false;
    Api.fetchShipmentStatuses()
      .then((data) => {
        if (cancelled) return;
        const list = extractPayload(data);
        setStatuses(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setStatuses([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const lines = useMemo(() => {
    if (!trackingData) return [];
    return Array.isArray(trackingData.lines) ? trackingData.lines : [];
  }, [trackingData]);
  const shipmentStatusValue = trackingData?.shipmentStatus || '';

  const timelineSteps = useMemo(() => {
    return statuses.map((status, index) => {
      const label =
        status.name || status.title || status.code || t('tracking.status_fallback');
      return {
        id: status.id || status.statusId || status.code || `status-${index}`,
        label,
        labelLower: (label || '').toLowerCase(),
      };
    });
  }, [statuses, t]);

  const activeStatusIndex = useMemo(() => {
    if (!shipmentStatusValue) return -1;
    const current = shipmentStatusValue.toLowerCase();
    return timelineSteps.findIndex((step) => step.labelLower === current);
  }, [timelineSteps, shipmentStatusValue]);

  const formatAmount = (value) => {
    if (value == null || Number.isNaN(Number(value))) {
      return t('tracking.unknown_value');
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
      }).format(Number(value));
    } catch {
      return `$${Number(value).toFixed(2)}`;
    }
  };

  const formatQuantityLabel = (value) => {
    const template = t('tracking.quantity_label');
    const safeValue = value ?? 1;
    if (typeof template === 'string' && template.includes('{{count}}')) {
      return template.replace('{{count}}', safeValue);
    }
    return `${template} ${safeValue}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = orderNumber.trim();
    if (!trimmed) {
      setError(t('tracking.number_required'));
      setTrackingData(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await Api.fetchOrderTracking(trimmed);
      const normalized = normalizeTracking(response);
      if (!normalized) {
        setTrackingData(null);
        setError(t('tracking.not_found'));
      } else {
        setTrackingData(normalized);
      }
    } catch {
      setTrackingData(null);
      setError(t('tracking.not_found'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tracking-page">
      <section className="tracking-hero">
        <div className="tracking-hero__logo">
          <a href="/" aria-label="Wassini Luxury">
            <img src={logo} alt="Wassini Luxury"  style={{width:"100px"}}/>
          </a>
        </div>
        <div>
          <p className="eyebrow">{t('tracking.eyebrow')}</p>
          <h1>{t('tracking.title')}</h1>
          <p className="tracking-subtitle">{t('tracking.subtitle')}</p>
        </div>
        <form className="tracking-form" onSubmit={handleSubmit}>
          <label>
            {t('tracking.number_label')}
            <input
              type="text"
              value={orderNumber}
              placeholder={t('tracking.number_placeholder')}
              onChange={(event) => setOrderNumber(event.target.value)}
            />
          </label>
          <button type="submit" disabled={loading}>
            {loading ? t('tracking.searching') : t('tracking.submit')}
          </button>
          {error ? <p className="tracking-error">{error}</p> : null}
        </form>
      </section>

      <section className="tracking-progress-section">
        <h2>{t('tracking.statuses_heading')}</h2>
        {timelineSteps.length ? (
          <div className="tracking-progress">
            {timelineSteps.map((step, index) => {
              const isActive =
                activeStatusIndex >= 0 ? index <= activeStatusIndex : false;
              return (
                <div
                  key={step.id}
                  className={`tracking-progress__step ${isActive ? 'is-active' : ''}`}
                >
                  <span className="tracking-progress__dot" />
                  {index !== timelineSteps.length - 1 ? (
                    <span className="tracking-progress__connector" />
                  ) : null}
                  <span className="tracking-progress__label">{step.label}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="tracking-empty">{t('tracking.statuses_empty')}</p>
        )}
      </section>

      <section className="tracking-results">
        <h2>{t('tracking.results_heading')}</h2>
        {!trackingData && !loading && !error ? (
          <p className="tracking-empty">{t('tracking.no_result')}</p>
        ) : null}
        {trackingData ? (
          <div className="tracking-card">
            <div className="tracking-card__summary tracking-card__summary--split">
              <div>
                <p className="tracking-card__label">{t('tracking.order_details')}</p>
                <p className="tracking-card__value">
                  #{trackingData.orderNumber || trackingData.orderId || orderNumber}
                </p>
                <p className="tracking-card__meta">
                  {t('tracking.order_date')}{' '}
                  <strong>
                    {trackingData.date
                      ? formatDateDisplay(trackingData.date)
                      : t('tracking.unknown_date')}
                  </strong>
                </p>
                <p className="tracking-card__meta">
                  {t('tracking.order_total')}{' '}
                  <strong>{formatAmount(trackingData.totalAmount)}</strong>
                </p>
                <p className="tracking-card__meta">
                  {t('tracking.order_payment')}{' '}
                  <strong>{trackingData.paymentMethod || t('tracking.unknown_value')}</strong>
                </p>
                {trackingData.notes ? (
                  <p className="tracking-card__meta">
                    {t('tracking.order_notes')}{' '}
                    <strong>{trackingData.notes}</strong>
                  </p>
                ) : null}
              </div>
              <div>
                <p className="tracking-card__label">{t('tracking.shipment_details')}</p>
                <p className="tracking-card__value">
                  {trackingData.shipmentNumber || t('tracking.unknown_value')}
                </p>
                <p className="tracking-card__meta">
                  {t('tracking.current_status')}{' '}
                  <strong>
                    {trackingData.shipmentStatus || t('tracking.status_fallback')}
                  </strong>
                </p>
                <p className="tracking-card__meta">
                  {t('tracking.last_update')}{' '}
                  <strong>
                    {trackingData.shipmentDate
                      ? formatDateDisplay(trackingData.shipmentDate)
                      : t('tracking.unknown_date')}
                  </strong>
                </p>
              </div>
            </div>
            {lines.length ? (
              <div className="tracking-orders">
                <h3>{t('tracking.lines_heading')}</h3>
                <ul className="tracking-order-items tracking-order-items--detailed">
                  {lines.map((line, index) => (
                    <li key={`${line.barcode || line.name || index}-${index}`}>
                      <div className="tracking-line__main">
                        {line.image ? (
                          <img src={line.image} alt={line.name || 'Line item'} />
                        ) : null}
                        <div>
                          <p className="tracking-item__name">
                            {line.name || t('tracking.product_fallback')}
                          </p>
                          <p className="tracking-item__meta">
                            {line.variant ? line.variant : ''}
                            {line.variant && line.quantity ? ' • ' : ''}
                            {line.quantity ? formatQuantityLabel(line.quantity) : ''}
                          </p>
                          {line.notes ? (
                            <p className="tracking-line__notes">{line.notes}</p>
                          ) : null}
                          {line.barcode ? (
                            <p className="tracking-line__barcode">
                              {t('tracking.barcode_label')}: {line.barcode}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="tracking-line__totals">
                        <strong>{formatAmount(line.total ?? line.price)}</strong>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="tracking-empty">{t('tracking.order_items_empty')}</p>
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default TrackingPage;
